// .mts, with only erasable TypeScript syntax, so scripts/validate-app-config.mjs can run it
// directly in Node (type stripping) in this CommonJS package.

export const APP_ENVS = ['INT', 'UAT', 'PROD'] as const;

export type AppEnv = (typeof APP_ENVS)[number];

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const;

export type LogLevelName = (typeof LOG_LEVELS)[number];

const FIELD_TYPES = {
  ENV: 'string',
  API_URL: 'string',
  LIFEWAY_ACCOUNT_URL: 'string',
  LIFEWAY_BASE_URL: 'string',
  MYLIFEWAY_BASE_URL: 'string',
  MYLIFEWAY_CREATE_ACCOUNT_PATH: 'string',
  INTERCOM_APP_ID: 'string',
  INTERCOM_AUTH_URL: 'string',
  GTM_APP_ID: 'string',
  FULLSTORY_ORG_ID: 'string',
  BUZZSPROUT_API_KEY: 'string',
  LOG_LEVEL: 'string',
  CURRICULUM_PRICING_VERSION: 'number',
  FEATURE_TOGGLES: 'object',
} as const;

type FieldKey = keyof typeof FIELD_TYPES;

type FieldValue = {
  string: string;
  number: number;
  object: Record<string, boolean>;
};

export type AppConfig = Omit<{ [K in FieldKey]: FieldValue[(typeof FIELD_TYPES)[K]] }, 'ENV' | 'LOG_LEVEL'> & {
  ENV: AppEnv;
  LOG_LEVEL: LogLevelName;
};

const FIELD_KEYS = Object.keys(FIELD_TYPES) as FieldKey[];

export const STRING_FIELDS = FIELD_KEYS.filter(key => FIELD_TYPES[key] === 'string');

function isAppEnv(value: unknown): value is AppEnv {
  return APP_ENVS.some(env => env === value);
}

function isLogLevel(value: unknown): value is LogLevelName {
  return LOG_LEVELS.some(level => level === value);
}

export function validateAppConfig(input: unknown): string[] {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return ['config must be a JSON object'];
  }

  const config = input as Record<string, unknown>;
  const errors: string[] = [];

  for (const key of FIELD_KEYS) {
    const type = FIELD_TYPES[key];
    if (!(key in config)) {
      errors.push(`missing "${key}"`);
    } else if (typeof config[key] !== type || config[key] === null) {
      errors.push(`"${key}" must be a ${type}`);
    }
  }

  for (const key of Object.keys(config)) {
    if (!(key in FIELD_TYPES)) {
      errors.push(`unknown key "${key}"`);
    }
  }

  if (typeof config.ENV === 'string' && !isAppEnv(config.ENV)) {
    errors.push(`"ENV" must be one of ${APP_ENVS.join(', ')}`);
  }

  if (typeof config.LOG_LEVEL === 'string' && !isLogLevel(config.LOG_LEVEL)) {
    errors.push(`"LOG_LEVEL" must be one of ${LOG_LEVELS.join(', ')}`);
  }

  if (config.FEATURE_TOGGLES && typeof config.FEATURE_TOGGLES === 'object') {
    for (const [toggle, value] of Object.entries(config.FEATURE_TOGGLES)) {
      if (typeof value !== 'boolean') {
        errors.push(`FEATURE_TOGGLES.${toggle} must be a boolean`);
      }
    }
  }

  return errors;
}
